import { DesignScheme } from '@data/buildingProjects/DesignSchemes/designScheme';
import { BlockKeys } from '@data/Simulations/pipeline/BlockCatalog';
import type { CloseToDesignSchemesBlockParameters } from '@data/Simulations/pipeline/blocks/CloseToDesignSchemesBlock';
import type { RemoveSensorsByIdBlockParameters } from '@data/Simulations/pipeline/blocks/RemoveSensorsBlock';
import type { ShadowBlockParameters } from '@data/Simulations/pipeline/blocks/ShadowBlock';
import { Pipeline } from '@data/Simulations/pipeline/Pipeline';
import { PipelineBlock } from '@data/Simulations/pipeline/PipelineBlock';
import { ControllerManager } from '@gui/controller/ControllerManager';
import usePersistentStore from '@gui/store/persistent/persistentStore';
import {
    buildCloseToDesignSchemesBlockForSet,
    getCloseToDesignSchemesMeshesForSet,
    buildShadowBlockForSet,
    getShadowMeshesForSet,
} from '@rendering/DesignSchemes/DesignSchemeUtils';
import { DesignSchemeSetState, LayerKind, PipelinePhase } from '@visualizer/types';
import { getGrid } from 'store/LoadingStore';
import { getViewer } from 'store/viewerStore';
import Grid from '../../../Geodata/grid';

export type RefreshDesignSchemePostBlocksOptions = {
    refreshGeometry?: boolean;
    refreshSensors?: boolean;
};

type DSPostBlocks = {
    shadowBlock?: PipelineBlock<ShadowBlockParameters>;
    keepoutBlock?: PipelineBlock<ShadowBlockParameters>;
    innerSpBlock?: PipelineBlock<RemoveSensorsByIdBlockParameters>;
    outerSpBlock?: PipelineBlock<RemoveSensorsByIdBlockParameters>;
    closeToDesignSchemesBlock?: PipelineBlock<CloseToDesignSchemesBlockParameters>;
};

type GetDesignSchemesForSet = (setId: string) => DesignScheme[] | undefined;

/**
 * Owns the hidden DesignScheme post-block synchronization with pipeline state.
 * It resolves the DS post blocks in the active pipeline(s), updates their params
 * and enabled state from the selected set, and persists pipeline changes back to the store.
 *
 */
export class DesignSchemePostBlockSync {
    private pendingGeometryRetryTimer: number | null = null;

    private get store() {
        return usePersistentStore.getState();
    }

    // =====================================
    // EXPOSED FUNCTION & HELPERS
    // =====================================

    private getSensorIds(designSchemes: DesignScheme[]): Set<number> {
        const sensorIds = new Set<number>();
        designSchemes.forEach((designScheme) => {
            designScheme.getSensorDatasFor(LayerKind.MAIN).forEach((sensorData) => {
                sensorIds.add(sensorData.getID());
            });
        });
        return sensorIds;
    }

    private getMultimodeSourceLayerIds(): string[] {
        try {
            return Array.from(
                new Set(
                    ControllerManager.getInstance()
                        .getMixerController()
                        .getMixerLayer()
                        .getInternalMixerElements()
                        .map((element) => element.layerId)
                )
            );
        } catch {
            return this.getAllLayerIds({ includeMixed: false });
        }
    }

    private getLayerIds(): string[] {
        const activeLayerId = this.store.layers.activeLayerID;
        if (activeLayerId && activeLayerId !== 'mixed') {
            return [activeLayerId];
        }

        if (activeLayerId === 'mixed') {
            if (this.store.global.data.multimode) {
                return this.getMultimodeSourceLayerIds();
            }
            return ['mixed'];
        }

        return this.getAllLayerIds({ includeMixed: false });
    }

    private getAllLayerIds({ includeMixed = false }: { includeMixed?: boolean } = {}): string[] {
        return Object.keys(this.store.layers.layerStates).filter((layerId) => includeMixed || layerId !== 'mixed');
    }

    private getLayerPipeline(layerId: string): Pipeline | null {
        try {
            return getGrid().searchLayerByUID(layerId).getPipeline();
        } catch {
            return null;
        }
    }

    private getPostBlocks(pipeline: Pipeline): DSPostBlocks {
        const postBlocks = pipeline.getBlocks().filter((block) => block.getPipelinePhase() === PipelinePhase.Post);
        const shadowBlocks = postBlocks.filter((block) => block.type === BlockKeys.Shadow) as Array<PipelineBlock<ShadowBlockParameters>>;
        const removeSensorBlocks = postBlocks.filter((block) => block.type === BlockKeys.RemoveSensorsById) as Array<
            PipelineBlock<RemoveSensorsByIdBlockParameters>
        >;
        const closeToDesignSchemesBlock = postBlocks.find((block) => block.type === BlockKeys.CloseToDesignSchemes) as
            | PipelineBlock<CloseToDesignSchemesBlockParameters>
            | undefined;

        return {
            shadowBlock: shadowBlocks.find((block) => !(block.getParams() as ShadowBlockParameters).isInverted),
            keepoutBlock: shadowBlocks.find((block) => (block.getParams() as ShadowBlockParameters).isInverted),
            innerSpBlock: removeSensorBlocks.find((block) => (block.getParams() as RemoveSensorsByIdBlockParameters).isInverted),
            outerSpBlock: removeSensorBlocks.find((block) => !(block.getParams() as RemoveSensorsByIdBlockParameters).isInverted),
            closeToDesignSchemesBlock,
        };
    }

    // Applies the pipeline changes in the correct way based on the current mode:
    private refreshCurrentViewSensors() {
        // 1) Apply post blocks to any "normal" layer:
        if (this.store.layers.activeLayerID !== 'mixed') {
            ControllerManager.getInstance().getPipelineController().run();
            return;
        }

        // 2) Apply post blocks to the Mixer:
        const mixerController = ControllerManager.getInstance().getMixerController();

        // 3) Refresh Multi layers as well:
        if (this.store.global.data.multimode) {
            mixerController.refreshInternalState();
            getViewer().getInstanceRenderer()?.updateFrames();
            getViewer().requestRender?.('ui');
            return;
        }

        mixerController.run();
    }

    private persistLayerPipeline(layerId: string, pipeline: Pipeline) {
        const prevExecutionCount = this.store.layers.layerStates[layerId]?.pipeline.executionCount ?? 0;
        this.store.updateLayerState(layerId, {
            pipeline: {
                ...pipeline.toJSON(),
                executionCount: prevExecutionCount,
            },
        });
    }

    private scheduleGeometryRetry(getDesignSchemesForSet: GetDesignSchemesForSet, refreshSensors: boolean, selectedSetId: string): boolean {
        // 1. If geometry is still pending, skip:
        if (this.pendingGeometryRetryTimer != null) {
            return false;
        }

        // 2 Schedule refresh retry (every 50ms until it actually goes through):
        this.pendingGeometryRetryTimer = window.setTimeout(() => {
            this.pendingGeometryRetryTimer = null;

            // 2.1. Abort if user switched to another DesignScheme set:
            const latestSelectedSetId = this.store.designSchemes.selectedSetId;
            if (latestSelectedSetId !== selectedSetId) {
                return;
            }

            // 2.2. Refresh sensors:
            this.refreshDesignSchemePostBlocks(getDesignSchemesForSet, {
                refreshGeometry: true,
                refreshSensors,
            });
        }, 50);

        return false;
    }

    // Main DS post-block refresh entrypoint.
    // 1) Reads the currently selected set and prepares the shared DS data needed for syncing.
    // 2) Syncs the hidden DS post blocks across all target layer pipeline(s) (if in multi).
    // 3) Persists changed pipelines and optionally reruns the active pipeline to refresh sensors.
    public refreshDesignSchemePostBlocks(
        getDesignSchemesForSet: GetDesignSchemesForSet,
        { refreshGeometry = false, refreshSensors = true }: RefreshDesignSchemePostBlocksOptions = {}
    ) {
        const selectedSetId = this.store.designSchemes.selectedSetId;
        const selectedSetState = selectedSetId ? this.store.designSchemes.sets[selectedSetId] : undefined;
        if (!selectedSetId || !selectedSetState) {
            return this.disableAllDesignSchemePostBlocks({ refreshSensors });
        }

        const designSchemes = getDesignSchemesForSet(selectedSetId) ?? [];
        if (designSchemes.length === 0) {
            return this.disableAllDesignSchemePostBlocks({ refreshSensors });
        }

        const shouldEnableSet = selectedSetState.visibility;
        const sensorsInDSs = this.getSensorIds(designSchemes);

        let shadowParams: Readonly<ShadowBlockParameters> | undefined;
        let keepoutParams: Readonly<ShadowBlockParameters> | undefined;
        let closeToDesignSchemesParams: Readonly<CloseToDesignSchemesBlockParameters> | undefined;
        let syncGeometryBlocks = true;

        if (refreshGeometry) {
            // Defer sensors refresh if any DS in the set is still being generated:
            const hasPendingGeometry = designSchemes.some((designScheme) => designScheme.getRenderer()?.isGenerating());
            if (hasPendingGeometry) {
                this.scheduleGeometryRetry(getDesignSchemesForSet, refreshSensors, selectedSetId);
                syncGeometryBlocks = false;
            } else {
                const shadowMeshGroups = getShadowMeshesForSet(selectedSetId);
                const closeToDesignSchemesMeshGroups = getCloseToDesignSchemesMeshesForSet(selectedSetId);
                shadowParams = buildShadowBlockForSet(designSchemes, shadowMeshGroups).getParams();
                keepoutParams = buildShadowBlockForSet(designSchemes, shadowMeshGroups, true).getParams();
                closeToDesignSchemesParams = buildCloseToDesignSchemesBlockForSet(
                    designSchemes,
                    closeToDesignSchemesMeshGroups,
                    Grid.gridRes
                ).getParams();
            }
        }

        const layerIds = this.getLayerIds();
        let didChange = false;

        for (const layerId of layerIds) {
            const pipeline = this.getLayerPipeline(layerId);
            if (!pipeline) continue;

            const blocks = this.getPostBlocks(pipeline);
            const didChangeInLayer = this.syncPipeline({
                pipeline,
                blocks,
                selectedSetState,
                shouldEnableSet,
                sensorsInsideDesignSchemes: sensorsInDSs,
                shadowParams,
                keepoutParams,
                closeToDesignSchemesParams,
                syncGeometryBlocks,
            });

            if (!didChangeInLayer) continue;

            this.persistLayerPipeline(layerId, pipeline);
            didChange = true;
        }

        if (refreshSensors && didChange) {
            this.refreshCurrentViewSensors();
        }

        return didChange;
    }

    // Disables DesignScheme Blocks when there are no more DesignSchemes or DesignSchemeSet available.
    public disableAllDesignSchemePostBlocks({ refreshSensors = true }: Pick<RefreshDesignSchemePostBlocksOptions, 'refreshSensors'> = {}) {
        let didChange = false;

        for (const layerId of this.getAllLayerIds({ includeMixed: true })) {
            const pipeline = this.getLayerPipeline(layerId);
            if (!pipeline) continue;

            const blocks = this.getPostBlocks(pipeline);
            let didChangeInLayer = false;
            didChangeInLayer = this.updateRemoveSensorsBlock(blocks.innerSpBlock, new Set<number>()) || didChangeInLayer;
            didChangeInLayer = this.updateRemoveSensorsBlock(blocks.outerSpBlock, new Set<number>()) || didChangeInLayer;
            didChangeInLayer = this.toggleBlockIfNeeded(pipeline, blocks.innerSpBlock, false) || didChangeInLayer;
            didChangeInLayer = this.toggleBlockIfNeeded(pipeline, blocks.outerSpBlock, false) || didChangeInLayer;
            didChangeInLayer = this.toggleBlockIfNeeded(pipeline, blocks.shadowBlock, false) || didChangeInLayer;
            didChangeInLayer = this.toggleBlockIfNeeded(pipeline, blocks.keepoutBlock, false) || didChangeInLayer;
            didChangeInLayer = this.toggleBlockIfNeeded(pipeline, blocks.closeToDesignSchemesBlock, false) || didChangeInLayer;

            if (!didChangeInLayer) continue;

            this.persistLayerPipeline(layerId, pipeline);
            didChange = true;
        }

        if (refreshSensors && didChange) {
            this.refreshCurrentViewSensors();
        }

        return didChange;
    }

    // =====================================
    // INTERNAL SYNC FUNCTION & HELPERS
    // =====================================

    private updateBlockParamsIfNeeded<T>(block: PipelineBlock<any> | undefined, params: Partial<T>): boolean {
        if (!block) return false;
        const previousParams = block.getParams() as Record<string, unknown>;
        const didChange = Object.entries(params as Record<string, unknown>).some(([key, value]) => previousParams[key] !== value);
        if (!didChange) return false;

        block.updateParams(params);
        return true;
    }

    private resolveSensorVisibilityBlockState(
        innerSP: boolean,
        outterSP: boolean,
        shouldEnableSet: boolean,
        closeToDesignSchemes: boolean
    ) {
        // Disable outer and inner blocks if CloseToDS is enabled (makes all 3 blocks independent):
        if (closeToDesignSchemes) {
            return {
                enableInnerBlock: false,
                enableOuterBlock: false,
            };
        }

        if (!shouldEnableSet || (innerSP && outterSP)) {
            return {
                enableInnerBlock: false,
                enableOuterBlock: false,
            };
        }

        return {
            enableInnerBlock: innerSP || (!innerSP && !outterSP),
            enableOuterBlock: outterSP || (!innerSP && !outterSP),
        };
    }

    private areSetsEqual(current?: ReadonlySet<number>, next?: ReadonlySet<number>) {
        if (!(current instanceof Set) || !(next instanceof Set)) {
            return current === next;
        }
        if (!current || !next) return current === next;
        if (current.size !== next.size) return false;
        for (const value of next) {
            if (!current.has(value)) return false;
        }
        return true;
    }

    private updateRemoveSensorsBlock(
        block: PipelineBlock<RemoveSensorsByIdBlockParameters> | undefined,
        sensorIds: ReadonlySet<number>
    ): boolean {
        if (!block) return false;
        const currentParams = block.getParams() as RemoveSensorsByIdBlockParameters;
        const currentSensorIds = currentParams.sensorIds instanceof Set ? currentParams.sensorIds : new Set<number>();
        if (this.areSetsEqual(currentSensorIds, sensorIds)) {
            return false;
        }

        block.updateParams({
            sensorIds: new Set<number>(sensorIds),
        } satisfies Partial<RemoveSensorsByIdBlockParameters>);
        return true;
    }

    private toggleBlockIfNeeded(pipeline: Pipeline, block: PipelineBlock<any> | undefined, enabled: boolean): boolean {
        if (!block) return false;
        if (block.isEnabled() === enabled) return false;
        return pipeline.toggleBlock(block.id, enabled);
    }

    // Syncs the pipeline (active layer generally) with the currently selected DesignScheme set.
    // 1) Updates Shadow/Keepout geometry params when DesignScheme geometry changes.
    // 2) Updates Inner/Outer SP sensor-id filters for this layer. (Safe to switch layers)
    // 3) Applies the selected set enabled/disabled state to the hidden DS post blocks.
    private syncPipeline({
        pipeline,
        blocks,
        selectedSetState,
        shouldEnableSet,
        sensorsInsideDesignSchemes,
        shadowParams,
        keepoutParams,
        closeToDesignSchemesParams,
        syncGeometryBlocks,
    }: {
        pipeline: Pipeline;
        blocks: DSPostBlocks;
        selectedSetState: DesignSchemeSetState | undefined;
        shouldEnableSet: boolean;
        sensorsInsideDesignSchemes: ReadonlySet<number>;
        shadowParams?: Readonly<ShadowBlockParameters>;
        keepoutParams?: Readonly<ShadowBlockParameters>;
        closeToDesignSchemesParams?: Readonly<CloseToDesignSchemesBlockParameters>;
        syncGeometryBlocks: boolean;
    }): boolean {
        let didChange = false;

        // 1. Refresh Shadow/Keepout geometry-driven params when runtime geometry is ready:
        if (syncGeometryBlocks) {
            didChange = this.updateBlockParamsIfNeeded(blocks.shadowBlock, shadowParams ?? {}) || didChange;
            didChange = this.updateBlockParamsIfNeeded(blocks.keepoutBlock, keepoutParams ?? {}) || didChange;
            didChange = this.updateBlockParamsIfNeeded(blocks.closeToDesignSchemesBlock, closeToDesignSchemesParams ?? {}) || didChange;
        }

        if (selectedSetState) {
            // 2. Refresh the paired sensor filters using the same DS sensor set plus inversion:
            const { enableInnerBlock, enableOuterBlock } = this.resolveSensorVisibilityBlockState(
                selectedSetState.innerSP,
                selectedSetState.outterSP,
                shouldEnableSet,
                selectedSetState.closeToDesignSchemes
            );

            didChange = this.updateRemoveSensorsBlock(blocks.innerSpBlock, sensorsInsideDesignSchemes) || didChange;
            didChange = this.updateRemoveSensorsBlock(blocks.outerSpBlock, sensorsInsideDesignSchemes) || didChange;
            didChange = this.toggleBlockIfNeeded(pipeline, blocks.innerSpBlock, enableInnerBlock) || didChange;
            didChange = this.toggleBlockIfNeeded(pipeline, blocks.outerSpBlock, enableOuterBlock) || didChange;
        } else {
            didChange = this.toggleBlockIfNeeded(pipeline, blocks.innerSpBlock, false) || didChange;
            didChange = this.toggleBlockIfNeeded(pipeline, blocks.outerSpBlock, false) || didChange;
        }

        // 3. Apply the selected set visibility flags to the DS geometry blocks only when geometry is ready:
        if (syncGeometryBlocks) {
            didChange = this.toggleBlockIfNeeded(pipeline, blocks.shadowBlock, shouldEnableSet && !!selectedSetState?.shadow) || didChange;
            didChange =
                this.toggleBlockIfNeeded(pipeline, blocks.keepoutBlock, shouldEnableSet && !!selectedSetState?.keepout) || didChange;
            didChange =
                this.toggleBlockIfNeeded(
                    pipeline,
                    blocks.closeToDesignSchemesBlock,
                    shouldEnableSet && !!selectedSetState?.closeToDesignSchemes
                ) || didChange;
        }

        return didChange;
    }
}
